import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import PropTypes from 'prop-types';

import { cancelNonRoutableRequest } from 'store/thunks/workQueueThunks';

import { NON_ROUTABLE_SOURCE } from '../constants/nonRoutableRequests';
import { useNonRoutableResolution } from '../hooks/useNonRoutableResolution';
import { nonRoutableRetryIsSafe } from '../utils/nonRoutableRequestErrors';
import NonRoutableRequestIdentity from './NonRoutableRequestIdentity';
import NonRoutableResolutionOutcome from './NonRoutableResolutionOutcome';

// IT NO LONGER CLAIMS THE ROW LEAVES THE QUEUE, and that is not a softening. With Cancelled
// ticked in the Status filter the queue refresh brings the row straight back, so the old wording
// told the operator to expect something they would then watch not happen.
const CANCELLED =
  'Cancelled. This prescription will never be filled. The request drops out of the default view, ' +
  'and stays visible while the Status filter includes Cancelled.';

// POM-6279: the cancel mutation re-reads relayOrdersMayExist, so the operator is told at the moment
// of cancelling — not only from the row they opened, which may be stale.
const successMessage = (detail) =>
  detail?.relayOrdersMayExist
    ? {
        variant: 'warning',
        message: `${CANCELLED} Relay orders for this reference may exist and were NOT cancelled — check the Relay tab.`,
      }
    : CANCELLED;

/**
 * CANCEL A PARKED REQUEST (POM-6223).
 *
 * Terminal, and the opposite decision to Bring Back: the prescription is never filled. No POM
 * order is created, nothing is published and NO PARTNER IS TOLD — the partner's own record still
 * says the prescription was transferred, and nothing this action does contradicts it. That is a
 * recorded product decision, and it is stated on the dialog because an operator reasonably
 * assumes cancelling notifies somebody.
 */
const CancelNonRoutableRequestDialog = ({ open, onClose, item }) => {
  const { submit, submitting, outcome, dismiss } = useNonRoutableResolution({
    item,
    action: cancelNonRoutableRequest,
    successMessage,
    onClose,
    // Resets the outcome each time the dialog is opened. The row menu keeps both dialogs mounted,
    // so without this a previous attempt's alert would still be on screen with no confirm button.
    open,
  });

  // Every dismissal goes through `dismiss`, which refreshes the queue on the way out — the
  // refetch is deliberately NOT done when the mutation returns, because this dialog lives inside
  // the row it is resolving and the refresh would unmount it mid-message.
  const handleClose = (_, reason) => {
    if (reason === 'backdropClick' || submitting) return;
    dismiss();
  };

  // Same rule as Bring Back: after an attempt, only a backend-confirmed "nothing happened, the
  // row is back where it started" earns another click.
  const canSubmit = !outcome || nonRoutableRetryIsSafe(outcome.described);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      aria-labelledby='CancelNonRoutableRequestDialog-title'
    >
      <DialogTitle id='CancelNonRoutableRequestDialog-title'>Cancel this request?</DialogTitle>
      <DialogContent>
        <NonRoutableRequestIdentity item={item} />

        <Alert severity='error'>
          <AlertTitle>This prescription will never be filled</AlertTitle>
          {/* An ESP / POM order has no partner record to contradict: it was placed in ESP or POM. */}
          {item.source === NON_ROUTABLE_SOURCE.ESP_POM ? (
            <>
              No order is created and nobody is notified —
              <strong> whoever placed this order in ESP or POM is not told</strong>. This cannot be
              undone.
            </>
          ) : (
            <>
              No order is created and
              <strong> the partner is not told</strong>. Their record will still show the
              prescription as transferred. This cannot be undone.
            </>
          )}
        </Alert>

        {/* POM-6279. Served from the relay orders themselves, never from the reason (a re-park can
            overwrite that). Cancelling this request ends the park only; it does not reach any
            relay order already written for the same reference. */}
        {item.relayOrdersMayExist && (
          <Alert
            severity='warning'
            sx={{ mt: 2 }}
            data-testid='non-routable-relay-orders-may-exist'
          >
            <AlertTitle>Relay orders may already exist for this reference</AlertTitle>
            Part of this order may already have been sent to a relay pharmacy. Cancelling this
            request does <strong>not</strong> cancel those relay orders — check the Relay tab before
            you cancel.
          </Alert>
        )}

        {outcome && <NonRoutableResolutionOutcome outcome={outcome} />}
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='secondary' onClick={dismiss} disabled={submitting}>
          {outcome && !canSubmit ? 'Close' : 'Keep the request'}
        </Button>
        {canSubmit && (
          <LoadingButton
            variant='contained'
            color='error'
            loading={submitting}
            onClick={submit}
            id={`CancelNonRoutableRequestDialog-${item.nonRoutableRequestId}-confirm`}
          >
            Cancel Request
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

CancelNonRoutableRequestDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  item: PropTypes.shape({
    nonRoutableRequestId: PropTypes.string.isRequired,
    referenceId: PropTypes.string,
    status: PropTypes.string,
    relayOrdersMayExist: PropTypes.bool,
    source: PropTypes.string,
  }).isRequired,
};

export default CancelNonRoutableRequestDialog;
